<template>
  <v-container>
    <v-row align="center" class="mb-4">
      <v-col cols="auto">
        <v-btn elevation="2" fab small @click="$router.go(-1)">
          <v-icon color="primary">mdi-arrow-left-bold</v-icon>
        </v-btn>
      </v-col>
      <v-col>
        <h1 class="text-h4 mb-0">Nastavenia upozornení</h1>
      </v-col>
    </v-row>

    <v-card class="mb-6">
      <v-card-title>Globálne nastavenia</v-card-title>
      <v-card-text>
        <div class="d-flex align-center mb-4">
          <span class="text-body-1 mr-4">Dostávať všetky upozornenia</span>
          <v-switch v-model="allEnabled" @change="toggleAll" :loading="loading" class="mt-0 pt-0" hide-details></v-switch>
        </div>

        <div class="d-flex align-center mb-2">
          <span class="text-body-1 mr-4">Emailové upozornenia</span>
          <v-switch v-model="emailEnabled" @change="toggleEmail" :loading="loading" :disabled="!allEnabled" class="mt-0 pt-0" hide-details></v-switch>
        </div>
        <p class="text-caption grey--text ms-4 mt-n2 mb-4" v-if="emailEnabled">
          Budete dostávať kópie upozornení na email.
        </p>
        <p class="text-caption grey--text ms-4 mt-n2 mb-4" v-else>
          Upozornenia uvidíte iba v aplikácii.
        </p>

        <v-divider class="my-4"></v-divider>
        <h3 class="text-subtitle-1 mb-2">Diskusia</h3>

        <div class="d-flex align-center mb-4">
          <span class="text-body-1 mr-4">Upozornenia o reakciách v diskusiách</span>
          <v-switch v-model="repliesEnabled" @change="toggleReplies" :loading="loading" :disabled="!allEnabled" class="mt-0 pt-0" hide-details></v-switch>
        </div>

        <div v-if="repliesEnabled && allEnabled" class="ml-4 mt-2">
          <p class="text-body-2 mb-2">Kedy vás máme upozorniť?</p>
          <v-radio-group v-model="replyTiming" @change="setTiming" :disabled="loading" column dense>
            <v-radio value="DELAYED" label="Oneskorene (24h)"></v-radio>
            <div class="text-caption grey--text ml-8 mb-2">
              Inteligentné oneskorenie: pošleme súhrn po 24h neaktivity. Odporúčané pre aktívne diskusie.
            </div>

            <v-radio value="IMMEDIATE" label="Ihneď"></v-radio>
            <div class="text-caption grey--text ml-8">
              Upozorníme vás ihneď po každej reakcii.
            </div>
          </v-radio-group>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mb-6">
      <v-card-title>Detailné nastavenia zdrojov</v-card-title>
      <v-card-text>
        <p class="mb-4 grey--text">
          Tu si môžete zapnúť alebo vypnúť upozornenia pre jednotlivé kategórie, ankety alebo moderátorov.
          Vypnutím (prepnutím doľava) zablokujete upozornenia z daného zdroja.
        </p>

        <v-expansion-panels v-model="expandedCategoryPanel" @change="onCategoryExpand" accordion>
          <v-expansion-panel v-for="category in allCategories" :key="category.id">
            <v-expansion-panel-header>
              <div class="d-flex align-center justify-space-between mr-4">
                <span>{{ category.name }}</span>
                <v-switch :input-value="!isCategoryBlocked(category.id)" @change="toggleCategory(category, $event)"
                  @click.native.stop class="ma-0 pa-0" hide-details dense></v-switch>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="category.polls">
                <div v-if="category.polls.length === 0" class="grey--text text-caption mb-2">
                  Žiadne ankety v tejto kategórii.
                </div>

                <v-list v-else dense>
                  <template v-for="poll in category.polls">
                    <v-list-group :key="poll.id" no-action sub-group>
                      <template v-slot:activator>
                        <v-list-item-content>
                          <v-list-item-title class="d-flex justify-space-between align-center">
                            <span>{{ poll.name }}</span>
                            <v-switch :input-value="!isPollBlocked(poll.id)" @change="togglePoll(poll, $event)"
                              @click.native.stop class="ma-0 pa-0 mr-4" hide-details dense></v-switch>
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>

                      <div v-if="poll.authors && poll.authors.length > 0" class="pl-4">
                        <v-subheader class="pl-4 pb-0" style="height: 32px;">Moderátori</v-subheader>
                        <v-list-item v-for="author in poll.authors" :key="author.id">
                          <v-list-item-content class="pl-4">
                            <v-list-item-title class="d-flex justify-space-between align-center">
                              <span class="text-caption">{{ author.first_name }} {{ author.last_name }}</span>
                              <v-switch :input-value="!isModeratorBlocked(author.id, poll.id)"
                                @change="toggleModerator(author, $event, poll)" class="ma-0 pa-0" hide-details
                                dense></v-switch>
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </div>
                      <v-list-item v-else>
                        <v-list-item-content>
                          <v-list-item-subtitle class="grey--text pl-4">Žiadni moderátori</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-group>
                  </template>
                </v-list>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { APIService } from "@/http/APIService";
const apiService = new APIService();

export default {
  name: 'NotificationSettings',
  data() {
    return {
      loading: false,
      allEnabled: true,
      repliesEnabled: true,
      emailEnabled: true,
      replyTiming: 'DELAYED',

      // Cache for expanded categories
      categoryDetails: {},
      expandedCategoryPanel: null,
      expandedPollPanel: null,
      loadingDetails: false,
      allCategories: []
    };
  },
  computed: {
    ...mapGetters('userNotifications', ['getPreferences']),
    ...mapGetters('userNotifications', ['getPreferences']),

    categories() {
      return this.allCategories;
    },

    blockedCategoriesIds() {
      if (!Array.isArray(this.getPreferences)) return [];
      return this.getPreferences.filter(p => p.blocked_category).map(p => p.blocked_category);
    },
    blockedPollsIds() {
      if (!Array.isArray(this.getPreferences)) return [];
      return this.getPreferences.filter(p => p.blocked_poll).map(p => p.blocked_poll);
    },
    blockedModeratorsIds() {
      if (!Array.isArray(this.getPreferences)) return [];
      return this.getPreferences.filter(p => p.blocked_moderator).map(p => p.blocked_moderator);
    }
  },
  created() {
    this.refreshPreferences();
    this.fetchAllCategories();
  },
  watch: {
    getPreferences: {
      immediate: true,
      handler(prefs) {
        if (prefs && prefs.length > 0) {
          // Global prefs usually in the first object that doesn't have specific blocks
          // Or simply the backend ensures one pref object holds these flags. 
          // Based on doc, getPreferences returns list. 
          // Let's find the "global" one (where blocks are null) or just use the first one if backend syncs them.
          // Safety check: find one without blocks or just take the first one.
          const globalPref = prefs.find(p => !p.blocked_category && !p.blocked_poll && !p.blocked_moderator) || prefs[0];

          if (globalPref) {
            if (typeof globalPref.all_notifications_disabled !== 'undefined') {
              this.allEnabled = !globalPref.all_notifications_disabled;
            }
            if (typeof globalPref.discussion_replies_disabled !== 'undefined') {
              this.repliesEnabled = !globalPref.discussion_replies_disabled;
            }
            if (typeof globalPref.email_notifications_enabled !== 'undefined') {
              this.emailEnabled = globalPref.email_notifications_enabled;
            }
            if (globalPref.discussion_reply_timing) {
              this.replyTiming = globalPref.discussion_reply_timing;
            }
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('userNotifications', ['fetchPreferences', 'deletePreference']),

    refreshPreferences() {
      this.fetchPreferences();
    },

    async toggleAll(val) {
      this.loading = true;
      try {
        await apiService.toggleAllNotifications(val);
        this.refreshPreferences();
      } catch (error) {
        console.error(error);
        this.allEnabled = !val;
      } finally {
        this.loading = false;
      }
    },

    async toggleReplies(val) {
      this.loading = true;
      try {
        await apiService.toggleDiscussionReplies(val);
        this.refreshPreferences();
      } catch (error) {
        console.error(error);
        this.repliesEnabled = !val;
      } finally {
        this.loading = false;
      }
    },

    async toggleEmail(val) {
      this.loading = true;
      try {
        await apiService.toggleEmailNotifications(val);
        this.refreshPreferences();
      } catch (error) {
        console.error(error);
        this.emailEnabled = !val;
      } finally {
        this.loading = false;
      }
    },

    async setTiming(val) {
      this.loading = true;
      try {
        await apiService.setDiscussionReplyTiming(val);
        this.refreshPreferences();
      } catch (error) {
        console.error(error);
        // revert logic if needed, but tricky with radio
      } finally {
        this.loading = false;
      }
    },

    isCategoryBlocked(id) {
      return this.blockedCategoriesIds.includes(id);
    },
    isPollBlocked(id) {
      return this.blockedPollsIds.includes(id);
    },
    isModeratorBlocked(authorId, pollId = null) {
      if (!Array.isArray(this.getPreferences)) return false;

      if (pollId) {
        // Check for specific block in this poll
        // The backend preference object for a scoped block should have blocked_moderator = authorId AND blocked_poll = pollId (or similar field)
        // Based on list view: "len v ankete: ..."
        // Let's check the preference structure.
        // A scoped preference has `blocked_moderator` (ID) and `blocked_moderator_in_poll` (ID or Object?).
        // In `blockedModerators` computed, we use `pref.blocked_moderator_in_poll_name`.
        // So `blocked_moderator_in_poll` is likely the field holding the poll ID or object.

        // Actually, let's look at the structure of preferences again or guess safely.
        // If I look at `blockedModerators` list item: `pref.blocked_moderator_in_poll_name`
        // The preference object likely has `blocked_poll` ID if it's a poll block, OR `blocked_moderator_in_poll` ID?
        // Let's assume `blocked_poll` is for BLOCKING THE POLL ITSELF.
        // For blocking a moderator IN a poll, it might be a different field or `blocked_poll` is used in combination?
        // The APIService `blockModerator` sends `poll_id`.
        // The resulting Preference object likely links the poll.
        // Let's search for a preference where blocked_moderator == authorId AND (blocked_poll == pollId OR blocked_moderator_in_poll == pollId)

        return this.getPreferences.some(p =>
          p.blocked_moderator === authorId &&
          (p.blocked_poll === pollId || p.blocked_moderator_in_poll === pollId)
        );
      } else {
        // Global block check (blocked_moderator set, but NO poll scope)
        return this.getPreferences.some(p =>
          p.blocked_moderator === authorId &&
          !p.blocked_poll && !p.blocked_moderator_in_poll
        );
      }
    },

    async toggleCategory(category, val) {
      // val is the NEW state (true = enabled/unblocked, false = disabled/blocked)
      // If val is true (Enabled), we UNBLOCK.
      // If val is false (Disabled), we BLOCK.
      try {
        if (val) {
          await apiService.unblockCategory(category.id);
        } else {
          await apiService.blockCategory(category.id);
        }
        this.refreshPreferences();
      } catch (error) {
        console.error(error);
      }
    },

    async togglePoll(poll, val) {
      try {
        if (val) {
          await apiService.unblockPoll(poll.id);
        } else {
          await apiService.blockPoll(poll.id);
        }
        this.refreshPreferences();
      } catch (error) {
        console.error(error);
      }
    },

    async toggleModerator(moderator, val, poll) {
      try {
        const pollId = poll ? poll.id : null;
        if (val) {
          await apiService.unblockModerator(moderator.id, pollId);
        } else {
          await apiService.blockModerator(moderator.id, pollId);
        }
        this.refreshPreferences();
      } catch (error) {
        console.error(error);
      }
    },

    async loadCategoryDetails(id) {
      if (!this.categoryDetails[id]) {
        this.loadingDetails = true;
        try {
          const response = await apiService.getGeneralPollCategory(id);
          // Reactively set property
          this.$set(this.categoryDetails, id, response);
        } catch (error) {
          console.error(error);
        } finally {
          this.loadingDetails = false;
        }
      }
    },

    onCategoryExpand(panelIndex) {
      // No need to fetch details, we have them in allCategories
    },

    async fetchAllCategories() {
      try {
        const response = await apiService.getGeneralPollCategories(null, '', 1000, 0);
        // The response contains rich data including polls and authors
        this.allCategories = response.results;
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    }
  }
};
</script>




